/**
 * GamesList.js
 * Display list of games with filtering and join functionality
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    CircularProgress,
    Alert,
    TextField,
    MenuItem,
    Grid,
    IconButton
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import gameManagementApi from './gameManagementApi';

export default function GamesList({ title, refreshTrigger, showJoinButton = true }) {
    const navigate = useNavigate();
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    useEffect(() => {
        loadGames();
    }, [refreshTrigger]);

    const loadGames = async () => {
        try {
            setLoading(true);
            setError(null);

            const filters = {};
            if (filterType) filters.game_type = filterType;
            if (filterStatus) filters.status = filterStatus;
            filters.limit = 50;

            const result = await gameManagementApi.listGames(filters);

            if (result.success) {
                setGames(result.games);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleJoinGame = async (gameId) => {
        const playerName = prompt('Enter your player name:');
        if (!playerName) return;

        try {
            const result = await gameManagementApi.joinGame(gameId, playerName);
            if (result.success) {
                alert(`Successfully joined as ${result.player_name}!\nAuth token: ${result.auth_token}`);
                // TODO: Navigate to game interface
            } else {
                alert(`Failed to join: ${result.error}`);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    const handleViewGame = (game) => {
        // Navigate to game step page with game name
        window.location.href = `/game-step?gameId=${encodeURIComponent(game.id)}`;
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
            case 'in_progress':
                return 'success';
            case 'waiting':
            case 'pending':
                return 'warning';
            case 'completed':
            case 'finished':
                return 'default';
            default:
                return 'info';
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    if (loading && games.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">
                    {title}
                </Typography>
                <IconButton onClick={loadGames} disabled={loading}>
                    <RefreshIcon />
                </IconButton>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Filters */}
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            label="Game Type"
                            value={filterType}
                            onChange={(e) => {
                                setFilterType(e.target.value);
                                setTimeout(loadGames, 100);
                            }}
                            size="small"
                        >
                            <MenuItem value="">All Types</MenuItem>
                            <MenuItem value="big-brother">Big Brother</MenuItem>
                            <MenuItem value="blood-on-the-clocktower">Blood on the Clocktower</MenuItem>
                            <MenuItem value="chat-game">Chat Game</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            label="Status"
                            value={filterStatus}
                            onChange={(e) => {
                                setFilterStatus(e.target.value);
                                setTimeout(loadGames, 100);
                            }}
                            size="small"
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="waiting">Waiting</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box display="flex" alignItems="center" height="100%">
                            <Typography variant="body2" color="text.secondary">
                                Total: {games.length} games
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Games Table */}
            {games.length === 0 ? (
                <Alert severity="info">
                    No games found. {title === 'My Games' ? 'Create your first game!' : 'Be the first to create a game!'}
                </Alert>
            ) : (
                <TableContainer component={Paper} elevation={2}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Game Name</strong></TableCell>
                                <TableCell><strong>Type</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell align="center"><strong>Players</strong></TableCell>
                                <TableCell align="center"><strong>Human</strong></TableCell>
                                <TableCell><strong>Created</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {games.map((game) => (
                                <TableRow key={game.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {game.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={game.game_type}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={game.status || 'Unknown'}
                                            size="small"
                                            color={getStatusColor(game.status)}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {game.player_count || 0}
                                    </TableCell>
                                    <TableCell align="center">
                                        {game.human_players || 0}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {formatDate(game.created_at)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewGame(game)}
                                                title="View Game"
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                            {showJoinButton && game.human_players > 0 && (
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleJoinGame(game.id)}
                                                    title="Join Game"
                                                >
                                                    <PlayArrowIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
